import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ClipboardList, 
  User, 
  ChevronRight, 
  Search,
  RefreshCw,
  Eye,
  CheckCircle2,
  Clock,
  AlertCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDS160Pendientes = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // Fetch cases that are in step 3 (Llenar Cuestionario) or have DS-160 data
      const res = await axios.get(`${API}/admin/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter cases that have questionnaire in progress (step 3) or have ds160_data
      const filteredCases = res.data.filter(c => 
        c.current_step >= 3 || c.ds160_data
      ).sort((a, b) => {
        // Sort by progress - those in step 3 first
        if (a.current_step === 3 && b.current_step !== 3) return -1;
        if (b.current_step === 3 && a.current_step !== 3) return 1;
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      
      setCases(filteredCases);
    } catch (error) {
      console.error("Error fetching cases:", error);
      toast.error("Error al cargar los casos");
    } finally {
      setLoading(false);
    }
  };

  const getDS160Progress = (caseData) => {
    const ds160 = caseData.ds160_data || {};
    const sections = [
      'personal_info', 'travel_info', 'travel_companions', 'previous_travel',
      'address_phone', 'passport_info', 'us_contact', 'family_info',
      'work_education', 'security_questions', 'additional_info'
    ];
    
    let completed = 0;
    sections.forEach(section => {
      if (ds160[section] && Object.keys(ds160[section]).length > 0) {
        completed++;
      }
    });
    
    return Math.round((completed / sections.length) * 100);
  };

  const filteredCases = cases.filter(c => {
    const clientName = c.client_name || c.user_name || "";
    const clientEmail = c.client_email || c.user_email || "";
    return clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           clientEmail.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-purple-600" />
              DS-160 Pendientes de Revisión
            </h1>
            <p className="text-navy-500 mt-1">
              Revisa las respuestas del cuestionario de tus clientes
            </p>
          </div>
          
          <Button
            onClick={fetchCases}
            variant="outline"
            className="flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
          <Input
            placeholder="Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Cases List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-navy-100">
            <ClipboardList className="w-12 h-12 mx-auto text-navy-300 mb-4" />
            <p className="text-navy-500">No hay casos con cuestionario pendiente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCases.map((caseData, index) => {
              const progress = getDS160Progress(caseData);
              const isInProgress = caseData.current_step === 3;
              
              return (
                <motion.div
                  key={caseData.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl border border-navy-100 p-4 hover:border-purple-200 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/admin/cases/${caseData.id}/ds160-review`)}
                  data-testid={`ds160-case-${caseData.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isInProgress ? 'bg-yellow-100' : progress >= 100 ? 'bg-green-100' : 'bg-purple-100'
                      }`}>
                        {isInProgress ? (
                          <Clock className="w-5 h-5 text-yellow-600" />
                        ) : progress >= 100 ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <ClipboardList className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-navy-900">
                          {caseData.client_name || caseData.user_name || "Sin nombre"}
                        </h3>
                        <p className="text-sm text-navy-500">
                          {caseData.client_email || caseData.user_email}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      {/* Progress */}
                      <div className="hidden md:block w-48">
                        <div className="flex justify-between text-xs text-navy-500 mb-1">
                          <span>Progreso DS-160</span>
                          <span>{progress}%</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>
                      
                      {/* Status Badge */}
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        isInProgress 
                          ? 'bg-yellow-100 text-yellow-700' 
                          : progress >= 100 
                            ? 'bg-green-100 text-green-700'
                            : 'bg-purple-100 text-purple-700'
                      }`}>
                        {isInProgress ? 'En Progreso' : progress >= 100 ? 'Completado' : `Etapa ${caseData.current_step}`}
                      </div>
                      
                      {/* View Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/cases/${caseData.id}/ds160-review`);
                        }}
                        data-testid={`view-ds160-${caseData.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Mobile Progress */}
                  <div className="md:hidden mt-3">
                    <div className="flex justify-between text-xs text-navy-500 mb-1">
                      <span>Progreso DS-160</span>
                      <span>{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        
        {/* Summary */}
        {!loading && filteredCases.length > 0 && (
          <div className="text-center text-sm text-navy-500">
            Mostrando {filteredCases.length} casos con cuestionario
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminDS160Pendientes;
